// src/components/NotificationSoftAsk.jsx
//
// Slim, dismissible in-app pre-prompt ("soft-ask") for push notification
// permission. NOT a full-screen / interstitial overlay — CLAUDE.md's ad-free
// rule forbids auto-triggered full-screen pitches. This is a non-blocking
// bottom bar concerning TournyFlex's OWN notifications (invites, friend
// requests, results), which is permitted.
//
// Why a soft-ask instead of firing the OS dialog directly: on iOS home-screen
// PWAs the native permission request must be triggered by a user gesture, and
// blindly auto-firing the OS dialog burns the one-and-only prompt a browser
// grants. Tapping "Enable" here is that gesture and reuses the exact same
// enablePush() flow the Me -> Notifications toggle uses (permission request +
// FCM token + push-register), so there is one code path, not two.
//
// Gating: only shown when shouldSoftAsk() is true (push configured + supported
// + Notification.permission === 'default') AND a localStorage cooldown hasn't
// been set by a recent "Not now". Re-checked on mount and on
// visibilitychange -> visible, mirroring App.jsx's ColdOpenToHome pattern so a
// user who backgrounds/foregrounds gets a fresh (cooldown-respecting) check.

import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { shouldSoftAsk, enablePush } from '../lib/push'

// One ask, ever. Dismissing the bar ("Not now") or going through the OS popup
// sets this flag permanently — we never re-nag. Owner directive: a user who
// declines or dismisses is not asked again; re-enabling is done only via
// Me -> Notifications. (A hard OS decline ALSO independently blocks it:
// shouldSoftAsk() is false once Notification.permission is 'denied'.)
const DISMISSED_KEY = 'tf:softAskDismissed'

// Route prefixes where the soft-ask must NEVER render: the active scoring /
// round surfaces. A notification bar over the live scorecard is launch-surface
// friction (CLAUDE.md: zero known-friction on the scorecard), and it must not
// re-appear when a user backgrounds mid-round (e.g. checks their gallery) and
// returns — the exact case the native app exists to protect. Mirrors the
// "don't disturb an active round" set in App.jsx's ColdOpenToHome /
// ResumeToActiveRound.
const ROUND_SURFACE_PREFIXES = ['/scorecard', '/round-leaderboard', '/shuffle', '/game']

function eligible() {
  if (!shouldSoftAsk()) return false
  try {
    if (localStorage.getItem(DISMISSED_KEY)) return false
  } catch {}
  return true
}

export default function NotificationSoftAsk() {
  const location = useLocation()
  const onRoundSurface = ROUND_SURFACE_PREFIXES.some((pre) => location.pathname.startsWith(pre))
  const [show, setShow] = useState(() => eligible())
  const [busy, setBusy] = useState(false)

  // Re-evaluate on foreground. If permission changed elsewhere (e.g. the user
  // enabled via Me -> Notifications), shouldSoftAsk() flips false and the bar
  // stays hidden. A permanent dismissal from a prior "Not now" is also respected.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') setShow(eligible())
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  if (!show || onRoundSurface) return null

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, '1') } catch {}
    setShow(false)
  }

  const onEnable = async () => {
    if (busy) return
    setBusy(true)
    try {
      await enablePush()
    } catch {
      /* enablePush swallows its own failures and returns { ok:false }; a thrown
         error here (network) shouldn't leave the bar stuck busy. */
    } finally {
      setBusy(false)
      // Whether granted or denied, permission is no longer 'default', so
      // shouldSoftAsk() is now false and the bar should not reappear. Set the
      // permanent dismiss flag too as a belt-and-suspenders guard, then hide.
      try { localStorage.setItem(DISMISSED_KEY, '1') } catch {}
      setShow(false)
    }
  }

  return (
    <div
      role="region"
      aria-label="Enable notifications"
      style={{
        position: 'fixed',
        left: 8,
        right: 8,
        // Sits just above the fixed .bottom-nav (60px + safe-area, see
        // index.css). z-index 90 keeps it above page content but below the
        // nav bar (z-index 100) so nav taps are never blocked.
        bottom: 'calc(72px + env(safe-area-inset-bottom))',
        zIndex: 90,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        background: 'var(--green-dark, #14231a)',
        border: '1px solid var(--gold, #D4B97A)',
        borderRadius: 'var(--radius, 10px)',
        boxShadow: '0 6px 20px rgba(0,0,0,0.28)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong style={{ fontSize: '0.85rem' }}>Turn on notifications</strong>
        <p className="text-muted text-xs" style={{ margin: '2px 0 0' }}>
          For round invites, friend requests, and results.
        </p>
      </div>
      <button
        onClick={dismiss}
        style={{
          padding: '6px 10px', background: 'none', color: 'var(--gray-400, #9aa4ae)',
          border: 'none', cursor: 'pointer', fontSize: '0.78rem', flexShrink: 0,
        }}
      >
        Not now
      </button>
      <button
        onClick={onEnable}
        disabled={busy}
        style={{
          padding: '6px 14px', background: 'var(--gold, #D4B97A)', color: 'var(--green-deep, #0A0F16)',
          border: 'none', borderRadius: 'var(--radius, 8px)', cursor: 'pointer',
          fontSize: '0.8rem', fontWeight: 600, flexShrink: 0, opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? 'Enabling…' : 'Enable'}
      </button>
    </div>
  )
}
